import { faArrowDownToLine } from '@fortawesome/sharp-solid-svg-icons';
import { useCallback, useMemo, useState } from 'react';

import Typography from '@/components/typography';
import { AUTH_ACTIONS } from '@/constants/constants';
import { CreateLeadListOption } from '@/features/selectionsPanel/exportMenu/createLeadListOption';
import { ExportMenuButton } from '@/features/selectionsPanel/exportMenu/exportMenuLink';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useToastContext } from '@/providers/ToastProvider';
import { bindLoadingStateToCallback } from '@/utils/bindLoadingStateToCallback';

export const SelectionDownloadOptions = () => {
  const {
    exportToCsv,
    exportToKml,
    canExportToCsv,
    canExportToKml,
    totalSelectionCount,
  } = useSelectionsPanelContext();
  const [isCsvLoading, setIsCsvLoading] = useState<boolean>(false);
  const [isKmlLoading, setIsKmlLoading] = useState<boolean>(false);

  const { showErrorToast, showSuccessToast } = useToastContext();

  const handleCsvClick = useCallback(async () => {
    try {
      await bindLoadingStateToCallback(exportToCsv, setIsCsvLoading);
      showSuccessToast('Successfully exported to CSV');
    } catch (error) {
      showErrorToast('Error exporting to CSV');
    }
  }, [exportToCsv, showErrorToast, showSuccessToast]);

  const handleKmlClick = useCallback(async () => {
    try {
      await bindLoadingStateToCallback(exportToKml, setIsKmlLoading);
      showSuccessToast('Successfully exported to KML');
    } catch (error) {
      showErrorToast('Error exporting to KML');
    }
  }, [exportToKml, showErrorToast, showSuccessToast]);

  const kmlTooltip = useMemo(() => {
    if (!canExportToKml) return AUTH_ACTIONS.UPGRADE_TO_ACCESS;
    if (!totalSelectionCount) return 'Make a Selection';
    return '';
  }, [canExportToKml, totalSelectionCount]);

  const csvTooltip = useMemo(() => {
    if (!canExportToCsv) return AUTH_ACTIONS.UPGRADE_TO_ACCESS;
    if (!totalSelectionCount) return 'Make a Selection';
    return '';
  }, [canExportToCsv, totalSelectionCount]);

  return (
    <div className="pt-4">
      <Typography className="px-4 pb-1" variant="button2">
        Downloads
      </Typography>
      <CreateLeadListOption />
      <ExportMenuButton
        disabled={!canExportToCsv || !totalSelectionCount}
        icon={faArrowDownToLine}
        iconClassName="text-gray-70"
        isLoading={isCsvLoading}
        label="CSV"
        tooltip={csvTooltip}
        onClick={handleCsvClick}
      />
      <ExportMenuButton
        disabled={!canExportToKml || !totalSelectionCount}
        icon={faArrowDownToLine}
        iconClassName="text-gray-70"
        isLoading={isKmlLoading}
        label="KML"
        tooltip={kmlTooltip}
        onClick={handleKmlClick}
      />
    </div>
  );
};
